import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Privilege, useCan } from '@feedflo/access-control';

// Our Own imports
import {
  buildMessageHistoryDescription,
  buildMessageHistoryLabel,
  buildMessageHistoryUrl,
} from '../../utils/messageHistoryUrl';

// Relative path imports
import './MessageHistoryLink.scss';

const VARIANTS = ['standalone', 'inline', 'compact'];

function MessageHistoryIconMark({ className = '' }) {
  return (
    <img
      className={`MessageHistoryLink-icon ${className}`.trim()}
      src="/icons/message-history.svg"
      alt=""
      aria-hidden="true"
    />
  );
}

MessageHistoryIconMark.propTypes = {
  className: PropTypes.string,
};

function MessageHistoryLink({
  filters = {},
  variant = 'standalone',
  label = '',
  description = '',
  className = '',
  title = '',
  onClick = () => {},
  children = null,
}) {
  const { canAnywhere, loading } = useCan();

  const canViewMessageHistory =
    canAnywhere('org', Privilege.ORG_USERS_MANAGE) ||
    canAnywhere('org', Privilege.ORG_GENERAL_ADMINISTRATE);

  const resolvedLabel = useMemo(() => {
    if (label) {
      return label;
    }

    if (typeof children === 'string' && children.trim()) {
      return children.trim();
    }

    return buildMessageHistoryLabel(filters);
  }, [children, filters, label]);

  const resolvedDescription = useMemo(() => {
    if (description) {
      return description;
    }

    return buildMessageHistoryDescription(filters);
  }, [description, filters]);

  const linkTitle = title || resolvedLabel;
  const url = buildMessageHistoryUrl(filters);

  if (loading || !canViewMessageHistory) {
    return null;
  }

  if (variant === 'compact') {
    return (
      <Link
        to={url}
        className={`MessageHistoryLink MessageHistoryLink--compact ${className}`.trim()}
        aria-label={resolvedLabel}
        title={linkTitle}
        onClick={onClick}
      >
        <MessageHistoryIconMark />
      </Link>
    );
  }

  if (variant === 'inline') {
    return (
      <Link
        to={url}
        className={`MessageHistoryLink MessageHistoryLink--inline ${className}`.trim()}
        aria-label={resolvedLabel}
        title={linkTitle}
        onClick={onClick}
      >
        <MessageHistoryIconMark />
        <span className="MessageHistoryLink-label">{resolvedLabel}</span>
      </Link>
    );
  }

  return (
    <div className={`MessageHistoryLink MessageHistoryLink--standalone ${className}`.trim()}>
      <Link
        to={url}
        className="MessageHistoryLink MessageHistoryLink--inline"
        aria-label={resolvedLabel}
        title={linkTitle}
        onClick={onClick}
      >
        <MessageHistoryIconMark />
        <span className="MessageHistoryLink-label">{resolvedLabel}</span>
      </Link>
      <p className="MessageHistoryLink-description">{resolvedDescription}</p>
    </div>
  );
}

MessageHistoryLink.propTypes = {
  filters: PropTypes.object,
  variant: PropTypes.oneOf(VARIANTS),
  label: PropTypes.string,
  description: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.node,
};

export default MessageHistoryLink;
